// In-product green information tutorials (the page tours opened from the green
// "i"), Measurement Core v1.3.12 §6.4:
//
//   tutorial_begin      the tour opens and its first step appears
//   tutorial_step_view  a step appears
//   tutorial_skip       the tour is left before Finish
//   tutorial_complete   Finish is selected on the final step
//
// GA4 in a browser, Firebase in the app; never AppsFlyer or ad platforms.
// tutorial_id is the stable controlled id of each tour (TUTORIALS below).
// step_number / total_steps are sent as text ("step_2", "steps_6"), Mantis
// note 15231.
import { useEffect, useRef } from "react";
import { trackClientEvent } from "./measurement";

const stepText = (n) => `step_${n}`;
const stepsText = (n) => `steps_${n}`;

export const TUTORIALS = {
  trade: { tutorial_id: "trade_tour", screen_name: "trade" },
  tradeMobile: { tutorial_id: "trade_tour_mobile", screen_name: "trade" },
  watchlist: { tutorial_id: "watchlist_tour", screen_name: "watchlist" },
  portfolio: { tutorial_id: "portfolio_tour", screen_name: "portfolio" },
  feed: { tutorial_id: "feed_tour", screen_name: "feed" },
  profile: { tutorial_id: "profile_tour", screen_name: "profile" },
  buySell: { tutorial_id: "buy_sell_tour", screen_name: "buy_sell" },
};

/**
 * A step counts as viewed once its bubble is actually on screen (`visible`), so
 * a step the tour skips because its target is missing is never logged, and a
 * rerender never logs twice. The first step that appears also logs
 * tutorial_begin. complete() / skip() each log once per opening; complete()
 * takes the shown total when a tour ends on a step whose target was missing.
 */
export const useTutorialMeasurement = ({ tutorial, totalSteps, stepIndex, visible }) => {
  const viewed = useRef(new Set());
  const ended = useRef(false);
  const latest = useRef({});
  latest.current = { totalSteps, stepIndex };

  useEffect(() => {
    if (!visible || ended.current || viewed.current.has(stepIndex)) return;
    if (viewed.current.size === 0) {
      trackClientEvent("tutorial_begin", { ...tutorial, total_steps: stepsText(totalSteps) });
    }
    viewed.current.add(stepIndex);
    trackClientEvent("tutorial_step_view", {
      ...tutorial,
      step_number: stepText(stepIndex + 1),
      total_steps: stepsText(totalSteps),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, stepIndex]);

  const end = (name, params) => {
    if (ended.current || viewed.current.size === 0) return;
    ended.current = true;
    trackClientEvent(name, { ...tutorial, ...params });
  };

  return {
    complete: (totalSteps = latest.current.totalSteps) =>
      end("tutorial_complete", { total_steps: stepsText(totalSteps) }),
    skip: () => end("tutorial_skip", { step_number: stepText(latest.current.stepIndex + 1) }),
  };
};
